import { requestKarangoApi } from "../../../core/api/karangoTransport";
import { invalidateCarpoolOffersCache } from "../../carpool/carpool.api";

const EVENT_ACCESS_CODE_HEADER = "X-Karango-Event-Access-Code";
const EVENT_ACCESS_CODE_STORAGE_PREFIX = "karango:event-access:";

function normalizeEventAccessCode(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, "")
    .toUpperCase();
}

function resolveEventAccessStorageKey(eventId) {
  const normalizedEventId = String(eventId || "").trim();
  return normalizedEventId ? `${EVENT_ACCESS_CODE_STORAGE_PREFIX}${normalizedEventId}` : "";
}

export function getStoredEventAccessCode(eventId) {
  const storageKey = resolveEventAccessStorageKey(eventId);

  if (!storageKey || typeof window === "undefined") {
    return "";
  }

  try {
    return normalizeEventAccessCode(window.sessionStorage.getItem(storageKey));
  } catch (error) {
    return "";
  }
}

export function storeEventAccessCode(eventId, accessCode) {
  const storageKey = resolveEventAccessStorageKey(eventId);
  const normalizedCode = normalizeEventAccessCode(accessCode);

  if (!storageKey || !normalizedCode || typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(storageKey, normalizedCode);
  } catch (error) {
    // sessionStorage peut etre indisponible en navigation privee stricte.
  }
}

export function clearStoredEventAccessCode(eventId) {
  const storageKey = resolveEventAccessStorageKey(eventId);

  if (!storageKey || typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(storageKey);
  } catch (error) {
    // Rien a faire : le prochain appel sans code restera verrouille.
  }
}

// ADR-005 §9 : au changement de compte ou a la deconnexion, aucun code
// d'acces saisi par le compte precedent ne doit rester dans l'onglet.
export function clearAllStoredEventAccessCodes() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const storage = window.sessionStorage;
    const keys = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && key.startsWith(EVENT_ACCESS_CODE_STORAGE_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch (error) {
    // sessionStorage indisponible : rien n'a pu y etre memorise.
  }
}

function buildEventAccessHeaders(eventId, explicitAccessCode) {
  const accessCode = normalizeEventAccessCode(
    explicitAccessCode || getStoredEventAccessCode(eventId)
  );

  return accessCode
    ? {
        [EVENT_ACCESS_CODE_HEADER]: accessCode,
      }
    : {};
}

const LIST_EVENTS_PAGE_SIZE = 50;

export async function listRemoteEventsPage({
  limit = LIST_EVENTS_PAGE_SIZE,
  cursor,
  signal,
  scope,
  lat,
  lng,
  radiusKm,
  timeScope,
  // Cutoff ISO 8601 fige par l'appelant (listRemoteEvents) et reutilise sur
  // toutes les pages d'une meme pagination, pour eviter qu'un evenement a la
  // frontiere n'apparaisse/disparaisse entre deux requetes HTTP successives
  // (cf. docs/map-performance-mobile-scroll-audit.md, C24).
  asOf,
  // DTO leger pour le menu Evenements (carte/liste/carrousel) — cf.
  // docs/event-menu-apple-like-performance-execution-spec.md, P0-API-01/phase 2.
  // Absent par defaut : tous les autres appelants (admin, historique, secret)
  // continuent de recevoir le DTO complet existant sans aucun changement.
  view,
  // Sorties d'un organisateur donne (section "Par l'organisateur" de la fiche
  // evenement) : ignore volontairement lat/lng/radiusKm cote serveur.
  organizerId,
  // Sorties auxquelles une personne est inscrite (section du profil,
  // docs/profile-participations-spec.md).
  participantId,
  // Classement explicite du flux de decouverte. `popular` est national ;
  // `nearest` utilise lat/lng sans imposer de rayon maximum.
  sort,
} = {}) {
  const params = new URLSearchParams();
  const normalizedScope = String(scope || "").trim().toLowerCase();
  const normalizedSort = String(sort || "").trim().toLowerCase();
  params.set("limit", String(limit));

  if (normalizedScope) {
    params.set("scope", normalizedScope);
  }

  if (String(view || "").trim().toLowerCase() === "discovery") {
    params.set("view", "discovery");
  }

  if (["date", "popular", "nearest"].includes(normalizedSort)) {
    params.set("sort", normalizedSort);
  }

  if (cursor) {
    params.set("cursor", String(cursor));
  }

  const hasValidPoint = Number.isFinite(lat) && Number.isFinite(lng);
  const hasValidRadius = Number.isFinite(radiusKm) && radiusKm > 0;

  if (normalizedSort === "nearest" && hasValidPoint) {
    params.set("lat", String(lat));
    params.set("lng", String(lng));
  } else if (
    (normalizedSort === "" || normalizedSort === "date") &&
    hasValidPoint &&
    hasValidRadius
  ) {
    params.set("lat", String(lat));
    params.set("lng", String(lng));
    params.set("radiusKm", String(radiusKm));
  }

  // Par defaut, le backend ne renvoie que les evenements en cours/a venir
  // ("active"). Les ecrans admin passent explicitement "all" pour continuer
  // a voir l'historique (cf. docs/map-performance-mobile-scroll-audit.md, C15).
  if (String(timeScope || "").trim().toLowerCase() === "all") {
    params.set("timeScope", "all");
  }

  if (asOf) {
    params.set("asOf", String(asOf));
  }

  const normalizedOrganizerId = String(organizerId || "").trim();
  if (normalizedOrganizerId) {
    params.set("organizerId", normalizedOrganizerId);
  }

  const normalizedParticipantId = String(participantId || "").trim();
  if (normalizedParticipantId) {
    params.set("participantId", normalizedParticipantId);
  }

  const requiresAuth = normalizedScope === "secret";
  const payload = await requestKarangoApi(`/v1/events?${params.toString()}`, {
    signal,
    requiresAuth,
    optionalAuth: !requiresAuth,
  });

  return {
    events: Array.isArray(payload?.events) ? payload.events : [],
    nextCursor: payload?.nextCursor || null,
  };
}

export async function listRemoteEvents({
  limit = 200,
  signal,
  scope,
  lat,
  lng,
  radiusKm,
  timeScope,
  // Appele apres chaque page resolue avec les events cumules jusqu'ici, pour
  // permettre un affichage progressif (premiere page visible sans attendre
  // les suivantes — cf. docs/map-performance-mobile-scroll-audit.md, Phase 3).
  onPage,
  view,
  sort,
} = {}) {
  const targetLimit = Math.max(0, Number.parseInt(String(limit || 0), 10) || 0);
  const events = [];
  let cursor = null;
  // Fige une seule fois pour toute la pagination (cf. C24).
  const asOf = new Date().toISOString();

  if (targetLimit === 0) {
    return events;
  }

  do {
    const remaining = targetLimit - events.length;
    const page = await listRemoteEventsPage({
      limit: Math.min(LIST_EVENTS_PAGE_SIZE, remaining),
      cursor,
      signal,
      scope,
      lat,
      lng,
      radiusKm,
      timeScope,
      asOf,
      view,
      sort,
    });

    events.push(...page.events);
    cursor = page.nextCursor;

    if (typeof onPage === "function" && !signal?.aborted) {
      onPage({ events: events.slice(), hasMore: Boolean(cursor) });
    }
  } while (cursor && events.length < targetLimit);

  return events;
}

// Prochaines sorties publiques d'un organisateur donne (section "Par
// l'organisateur" de la fiche evenement) : ignore volontairement le
// perimetre geographique/les filtres de carte de l'appelant — cf.
// listPublicEventsDiscovery organizerId cote serveur. Une seule page suffit
// (limite plafonnee sous LIST_EVENTS_PAGE_SIZE).
export async function listRemoteEventsByOrganizer(organizerId, { limit = 20, signal } = {}) {
  const normalizedOrganizerId = String(organizerId || "").trim();

  if (!normalizedOrganizerId) {
    return [];
  }

  const page = await listRemoteEventsPage({
    limit: Math.min(limit, LIST_EVENTS_PAGE_SIZE),
    signal,
    view: "discovery",
    organizerId: normalizedOrganizerId,
  });

  return page.events;
}

// Prochaines sorties auxquelles une personne est inscrite (confirmee), hors
// celles qu'elle organise. Vide si elle a choisi de les masquer.
export async function listRemoteEventsByParticipant(participantId, { limit = 20, signal } = {}) {
  const normalizedParticipantId = String(participantId || "").trim();

  if (!normalizedParticipantId) {
    return [];
  }

  const page = await listRemoteEventsPage({
    limit: Math.min(limit, LIST_EVENTS_PAGE_SIZE),
    signal,
    view: "discovery",
    participantId: normalizedParticipantId,
  });

  return page.events;
}

const LIST_MY_EVENTS_PAGE_SIZE = 100;

// Historique personnel (Agenda) : sorties organisees/rejointes par le viewer,
// toutes dates confondues. Distinct de listRemoteEvents (decouverte active
// uniquement) — cf. docs/map-performance-mobile-scroll-audit.md, C21.
export async function listMyRemoteEventsPage({
  limit = LIST_MY_EVENTS_PAGE_SIZE,
  cursor,
  signal,
} = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (cursor) {
    params.set("cursor", String(cursor));
  }

  const payload = await requestKarangoApi(`/v1/events/mine?${params.toString()}`, {
    signal,
    requiresAuth: true,
  });

  return {
    events: Array.isArray(payload?.events) ? payload.events : [],
    nextCursor: payload?.nextCursor || null,
  };
}

// Charge UNE page d'historique en partant de `cursor` (ou la 1ere page si
// absent) et renvoie `hasMore`, pour piloter un "charger plus" explicite dans
// l'Agenda plutot qu'un plafond silencieux a 300 (cf. section 21/22 du
// contre-audit : "Le frontend s'arrete silencieusement a 300 evenements").
export async function loadMoreMyRemoteEvents({
  cursor = null,
  limit = LIST_MY_EVENTS_PAGE_SIZE,
  signal,
} = {}) {
  const page = await listMyRemoteEventsPage({ limit, cursor, signal });
  return {
    events: page.events,
    nextCursor: page.nextCursor,
    hasMore: Boolean(page.nextCursor),
  };
}

// Conserve pour compatibilite (admin/tests) : accumule jusqu'a `limit` events
// en suivant le curseur. Pour l'Agenda, preferer `loadMoreMyRemoteEvents` qui
// expose `hasMore` au lieu de plafonner silencieusement.
export async function listMyRemoteEvents({ limit = 300, signal } = {}) {
  const targetLimit = Math.max(0, Number.parseInt(String(limit || 0), 10) || 0);
  const events = [];
  let cursor = null;

  if (targetLimit === 0) {
    return events;
  }

  do {
    const remaining = targetLimit - events.length;
    const page = await listMyRemoteEventsPage({
      limit: Math.min(LIST_MY_EVENTS_PAGE_SIZE, remaining),
      cursor,
      signal,
    });

    events.push(...page.events);
    cursor = page.nextCursor;
  } while (cursor && events.length < targetLimit);

  return events;
}

// Detail personnel autorise (Agenda / "Recreer") : fiche complete pour
// n'importe quel statut/visibilite tant que le viewer en est organisateur ou
// participant confirme — distinct de getRemoteEvent qui n'accepte que les
// evenements publics actifs (cf. section 21/22 du contre-audit).
export async function getMyRemoteEventById(eventId, { signal } = {}) {
  if (!eventId) {
    return null;
  }

  const payload = await requestKarangoApi(
    `/v1/events/mine/${encodeURIComponent(String(eventId))}`,
    { signal, requiresAuth: true }
  );

  return payload?.event || null;
}

export async function getRemoteEvent(eventId, { signal, accessCode } = {}) {
  if (!eventId) {
    return null;
  }

  const payload = await requestKarangoApi(
    `/v1/events/${encodeURIComponent(String(eventId))}`,
    {
      signal,
      headers: buildEventAccessHeaders(eventId, accessCode),
      optionalAuth: true,
    }
  );

  return payload?.event || null;
}

export async function createRemoteEvent(eventPayload = {}) {
  const payload = await requestKarangoApi("/v1/events", {
    method: "POST",
    requiresAuth: true,
    body: eventPayload,
  });

  return payload?.event || null;
}

export async function updateRemoteEvent(eventId, eventPatch = {}) {
  if (!eventId) {
    throw new Error("eventId requis.");
  }

  const payload = await requestKarangoApi(`/v1/events/${encodeURIComponent(String(eventId))}`, {
    method: "PATCH",
    requiresAuth: true,
    body: eventPatch,
  });

  return payload?.event || null;
}

export async function toggleEventInterest(eventId, status) {
  if (!eventId) throw new Error("eventId requis.");
  return requestKarangoApi(
    `/v1/events/${encodeURIComponent(String(eventId))}/interest`,
    {
      method: "POST",
      requiresAuth: true,
      body: { status },
    }
  );
}

export async function listMyEventInterestMap(eventIds = [], { signal } = {}) {
  const params = eventIds.length ? `?ids=${eventIds.map(encodeURIComponent).join(",")}` : "";
  const payload = await requestKarangoApi(`/v1/events/my-interest-map${params}`, {
    requiresAuth: true,
    signal,
  });
  return payload?.interests || {};
}

// ADR-016 : diffusion d'une sortie choisie par son organisateur.
export async function getEventDiffusion(eventId, { signal } = {}) {
  if (!eventId) throw new Error("eventId requis.");
  const payload = await requestKarangoApi(
    `/v1/events/${encodeURIComponent(String(eventId))}/diffusion`,
    { requiresAuth: true, signal }
  );
  return payload?.diffusion || null;
}

export async function requestEventDiffusion(eventId, scopes = []) {
  if (!eventId) throw new Error("eventId requis.");
  const payload = await requestKarangoApi(
    `/v1/events/${encodeURIComponent(String(eventId))}/diffusion`,
    { method: "POST", requiresAuth: true, body: { scopes } }
  );
  return payload?.diffusion || null;
}

export async function listMyInterestedEvents({ status = "INTERESTED", signal } = {}) {
  const payload = await requestKarangoApi(
    `/v1/events/my-interests?status=${encodeURIComponent(status)}`,
    { requiresAuth: true, signal }
  );
  return Array.isArray(payload?.events) ? payload.events : [];
}

export async function getMyEventParticipation(eventId, { signal } = {}) {
  if (!eventId) {
    throw new Error("eventId requis.");
  }

  return requestKarangoApi(
    `/v1/events/${encodeURIComponent(String(eventId))}/participants/me`,
    {
      requiresAuth: true,
      signal,
      headers: buildEventAccessHeaders(eventId),
    }
  );
}

export async function listMyEventParticipationMap(eventIds = [], { signal } = {}) {
  const params = eventIds.length ? `?ids=${eventIds.map(encodeURIComponent).join(",")}` : "";
  const payload = await requestKarangoApi(`/v1/events/my-participation-map${params}`, {
    requiresAuth: true,
    signal,
  });

  return payload?.participations || {};
}

export async function joinRemoteEvent(eventId, payload) {
  if (!eventId) {
    throw new Error("eventId requis.");
  }

  try {
    return await requestKarangoApi(
      `/v1/events/${encodeURIComponent(String(eventId))}/participants/me/join`,
      {
        method: "POST",
        ...(payload ? { body: payload } : {}),
        requiresAuth: true,
        headers: buildEventAccessHeaders(eventId),
      }
    );
  } finally {
    invalidateCarpoolOffersCache(eventId);
  }
}

export async function cancelRemoteEventParticipation(eventId, payload = {}) {
  if (!eventId) {
    throw new Error("eventId requis.");
  }

  try {
    return await requestKarangoApi(
      `/v1/events/${encodeURIComponent(String(eventId))}/participants/me/cancel`,
      { method: "POST", requiresAuth: true, body: payload }
    );
  } finally {
    invalidateCarpoolOffersCache(eventId);
  }
}

export async function listEventParticipants(eventId, { status, signal } = {}) {
  if (!eventId) {
    return [];
  }

  const params = new URLSearchParams();

  if (status) {
    params.set("status", String(status));
  }

  const query = params.toString();
  const payload = await requestKarangoApi(
    `/v1/events/${encodeURIComponent(String(eventId))}/participants${query ? `?${query}` : ""}`,
    {
      requiresAuth: true,
      signal,
    }
  );

  return Array.isArray(payload?.participants) ? payload.participants : [];
}

export async function approveRemoteParticipant(eventId, userId, payload = {}) {
  if (!eventId) {
    throw new Error("eventId requis.");
  }

  if (!userId) {
    throw new Error("userId requis.");
  }

  return requestKarangoApi(
    `/v1/events/${encodeURIComponent(String(eventId))}/participants/${encodeURIComponent(String(userId))}/approve`,
    {
      method: "POST",
      requiresAuth: true,
      body: payload,
    }
  );
}

export async function rejectRemoteParticipant(eventId, userId, payload = {}) {
  if (!eventId) {
    throw new Error("eventId requis.");
  }

  if (!userId) {
    throw new Error("userId requis.");
  }

  return requestKarangoApi(
    `/v1/events/${encodeURIComponent(String(eventId))}/participants/${encodeURIComponent(String(userId))}/reject`,
    {
      method: "POST",
      requiresAuth: true,
      body: payload,
    }
  );
}

export async function removeRemoteParticipant(eventId, userId, payload = {}) {
  if (!eventId) {
    throw new Error("eventId requis.");
  }

  if (!userId) {
    throw new Error("userId requis.");
  }

  return requestKarangoApi(
    `/v1/events/${encodeURIComponent(String(eventId))}/participants/${encodeURIComponent(String(userId))}/remove`,
    {
      method: "POST",
      requiresAuth: true,
      body: payload,
    }
  );
}
